import { useState, type CSSProperties } from 'react';
import { motion, MotionConfig } from 'motion/react';
import type { ChatUi } from '@voll-intelligence/genui-contract';
import { AirplaneTakeoffIcon, CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { fmtDateRange, fmtMoney } from '../../../shared/trip/trip_format.js';
import { MODAL_UI, modalLabel } from '../../../shared/trip/trip_maps.js';
import { ModalIcon, TripStatusBadge } from '../../../shared/trip/trip_primitives.js';
import { TripDrawer } from './components/TripDrawer.js';
import {
  isHiddenInStack,
  stackStagger,
  stackedCardStyle,
  useStackedDeck,
} from '../../../shared/ui/stacked_deck.js';
import './trip_ui.css';

type Trip = Extract<ChatUi, { kind: 'trips' }>['trips'][number];

// Curva e duração do glide stacked↔lista (mesma sensação do handoff). O `motion`
// faz o FLIP da mudança de posição (prop `layout`); o stagger entra como delay.
const GLIDE = { duration: 0.45, ease: [0.22, 0.61, 0.36, 1] as const };

/** Card individual de viagem — clicável; o que o clique faz depende do estado da pilha. */
function TripCard({
  trip,
  style,
  opacity,
  delay,
  tabIndex,
  onClick,
}: {
  readonly trip: Trip;
  readonly style?: CSSProperties;
  readonly opacity: number;
  readonly delay: number;
  readonly tabIndex?: number;
  readonly onClick: () => void;
}) {
  const { t, locale } = useGenUiI18n();
  const dateRange = fmtDateRange(trip.startLocal, trip.endLocal, trip.startAt, trip.endAt, locale);
  const total = fmtMoney(trip.totalAmount, trip.currency);
  const hasRoute = trip.origin != null && trip.destination != null;

  return (
    <motion.button
      type="button"
      className="tcard"
      data-trip={trip.tripId}
      style={style}
      tabIndex={tabIndex}
      onClick={onClick}
      layout
      initial={false}
      animate={{ opacity }}
      transition={{ layout: { ...GLIDE, delay }, opacity: { duration: 0.3, delay } }}
    >
      <div className="tcard__top">
        <span className="tcard__title">{trip.name ?? t('trips.unnamed')}</span>
        <span className="tcard__badge">
          <TripStatusBadge status={trip.status} />
        </span>
      </div>

      {hasRoute ? (
        <div className="tcard__route">
          <AirplaneTakeoffIcon size={12} aria-hidden />
          {trip.origin} → {trip.destination}
        </div>
      ) : (
        <div className="tcard__route is-faint">{t('trips.routeTbd')}</div>
      )}

      <div className="tcard__foot">
        <div className="tcard__modals">
          {trip.modals.map((m) => {
            const meta = MODAL_UI[m];
            if (!meta) return null;
            return (
              <span key={m} className={`tmod tmod--${m}`} title={modalLabel(m, t)}>
                <ModalIcon icon={meta.icon} size={13} />
              </span>
            );
          })}
        </div>
        {dateRange && <span className="tcard__dates">{dateRange}</span>}
        <span className="tcard__right">
          {total == null ? (
            <span className="tcard__total is-faint">{t('trips.totalTbd')}</span>
          ) : (
            <span className="tcard__total">{total}</span>
          )}
          <span className="tcard__go">
            <CaretRightIcon size={15} aria-hidden />
          </span>
        </span>
      </div>
    </motion.button>
  );
}

/**
 * Deck de viagens (`list_trips`): por padrão os cards ficam EMPILHADOS (pilha
 * compacta) e expandem numa lista vertical ao tocar no deck ou no botão. Tocar
 * num card já expandido abre o detalhe (`TripDrawer`). Com uma única viagem não
 * há pilha — renderiza o card direto. As viagens chegam já ordenadas por
 * proximidade (ordenação server-side no deriver do agente).
 *
 * A transição pilha↔lista é do `motion` (prop `layout` em cada card); o
 * `MotionConfig reducedMotion="user"` respeita `prefers-reduced-motion`.
 */
export function TripsDeck({ trips }: { readonly trips: Trip[] }) {
  const { t } = useGenUiI18n();
  const [openId, setOpenId] = useState<string | null>(null);
  const { expanded, stacked, listRef, expand, toggle } = useStackedDeck(trips.length, '.tcard');
  const open = openId == null ? undefined : trips.find((t) => t.tripId === openId);

  return (
    <MotionConfig reducedMotion="user">
      <div className={`tstack ${stacked ? 'is-stacked' : 'is-open'}`}>
        {/* Empilhado: o clique no deck (inclui as bordas dos cards de trás) expande. */}
        <div
          className="tcard-list"
          ref={listRef}
          onClick={stacked ? expand : undefined}
          // Área de clique redundante: quem expande com teclado é o botão
          // `.tstack__more` (com `aria-expanded`) mais abaixo. Este wrapper só
          // amplia o alvo de MOUSE pras bordas dos cards de trás, então não tem
          // ação própria a expor ao teclado.
          role="presentation"
        >
          {trips.map((trip, i) => (
            <TripCard
              key={trip.tripId}
              trip={trip}
              style={stacked ? stackedCardStyle(i, trips.length) : undefined}
              // Empilhado, os cards atrás dos visíveis somem (opacidade animada).
              opacity={stacked && isHiddenInStack(i) ? 0 : 1}
              delay={stackStagger(i, trips.length, expanded)}
              // Empilhado, só o card do topo é interativo (os de trás têm
              // pointer-events:none via CSS) — tira os ocultos da ordem de tab.
              tabIndex={stacked && i > 0 ? -1 : undefined}
              onClick={() => {
                if (!stacked) setOpenId(trip.tripId);
              }}
            />
          ))}
        </div>

        {trips.length > 1 && (
          <button type="button" className="tstack__more" aria-expanded={expanded} onClick={toggle}>
            {expanded ? t('trips.collapse') : t('trips.seeAll', { count: trips.length })}
            <CaretDownIcon size={13} aria-hidden />
          </button>
        )}

        {open && (
          <TripDrawer
            trip={open}
            onClose={() => {
              setOpenId(null);
            }}
          />
        )}
      </div>
    </MotionConfig>
  );
}
